import { es6exif } from "../lib/es6-exif.js";
import { getNestedProperty, setNestedProperty } from "../lib/helpers.js";
import db from "../db.js";
export const mimeType = "image/webp";

function _checkInspectionId(inspectionId) {
	if (!inspectionId) throw Error("Invalid argument 'inspectionId'. It should contain a valid inspection id.");
}

function getNewPictureId() {
	const max = 15;
	const randomPart = String(Math.random()).slice(2, max + 2);
	return Number(randomPart);
}

export async function getNewImageId() {
	let newId;

	do {
		newId = getNewPictureId();
	} while ((await db.images.get(newId)));

	return newId;
}

export async function getNewDrawingId() {
	let newId;

	do {
		newId = getNewPictureId();
	} while ((await db.drawings.get(newId)));

	return newId;
}

export async function getContextsIndexForInsp(insp, contextType) {
	let ct = "connected_" + contextType;
	insp = { ...insp, type: {} };
	let index = findByProp(insp, ct);
	return index;
}

export async function getContextsIndex(inspectionId, contextType) {
	let ct = "connected_" + contextType;
	if (!db.inspections) return {};
	let insp = await db.inspections.get(inspectionId);
	insp = { ...insp, type: {} };
	let index = findByProp(insp, ct);
	return index;
}

function makeIndex(ctx, ids) {
	let ind = { ...ids };

	for (let p in ind)
		ind[p] = ctx;


	return ind;
}

export function findByProp(o, prop, path = "") {
	let index = {};

	if (o) {
		if (o.hasOwnProperty(prop)) {
			(index = { ...index, ...makeIndex(path, o[prop]) });
		}
		for (let p in o) {
			if (typeof o[p] === "object" && o[p] !== null) {
				if (o[p].hasOwnProperty(prop)) {
					index = { ...index, ...makeIndex(path, o[p][prop]) };
				}
				var i = findByProp(o[p], prop, path === "" ? p : path + "." + p);

				index = { ...index, ...i };
			}
		}
	}
	return index;
}

export function getAllImages(inspectionId) {
	if (!db.images) return Promise.resolve([]);
	return db.images.where({ inspectionId: inspectionId }).toArray();
}

export function getAllDrawings(inspectionId) {
	if (!db.drawings) return Promise.resolve([]);
	return db.drawings.where({ inspectionId: inspectionId }).toArray();
}

function _convertImageToBase64Jpeg(img) {
	if (!img) {
		return Promise.resolve();
	}
	return new Promise((resolve, reject) => {
		const blob = new Blob([img.data], { type: img.dataMimeType });
		const i = new Image();
		i.onload = function (e) {
			var c = document.createElement("canvas");
			var ctx = c.getContext("2d");
			c.width = i.naturalWidth;
			c.height = i.naturalHeight;
			ctx.drawImage(i, 0, 0, i.naturalWidth, i.naturalHeight);

			URL.revokeObjectURL(i.src);

			const resImg = { ...img, dataUrl: c.toDataURL('image/jpeg', 0.85), data: null };
			
			c.remove();
			c = null;
			resolve(resImg);
		};
		i.onerror = (e) => reject(e);
		i.src = URL.createObjectURL(blob);
	});
}

export function getImageUrlInBase64(id) {
	return getImageById(id)
		.then(img => {
			return _convertImageToBase64Jpeg(img);
		})
		.then(convertedImg => {
			return convertedImg ? convertedImg.dataUrl : null;
		})
		.catch(a => console.log(a));
}

export function getImageById(id) {
	return db.images.get({ id });
}

export function getDrawingById(id) {
	return db.drawings.get({ id });
}

function getCoverImage(inspectionId) {
	let covImgIdProm = db.inspections.get(inspectionId).then(i => {
		if (i.cover_image_id) {
			return db.images.get(Number(i.cover_image_id));
		}
	});

	return covImgIdProm
		.then(img => img ? img : db.images.where('inspectionId').equals(inspectionId).first());
}

function convertPhoto(file) {
	return new Promise(function (resolve, reject) {
		const img = new Image();
		img.onload = function (e) {
			let checkOrientationPromise = Promise.resolve();
			if (file.type === "image/jpeg") {
				let isBrowserSupportExifOrientationHandling = (() => {
					const i = document.createElement('img');
					i.style.display = 'none';
					document.body.appendChild(i);
					const imageOrientation = window.getComputedStyle(i).imageOrientation;
					document.body.removeChild(i);
					return (imageOrientation === 'from-image');
				})();
				if (!isBrowserSupportExifOrientationHandling) {
					checkOrientationPromise = new Promise((resolve, reject) => {
						const exif = es6exif();
						exif.getData(img, function () {
							const orientation = exif.getTag(this, "Orientation");
							resolve(orientation);
						});
					});
				}
			}
			checkOrientationPromise.then(orientation => {
				const c = document.createElement("canvas");
				const ctx = c.getContext("2d");

				let getScale = (w, h) => {
					// limit image size with HD resolution (1920 x 1080)
					const hRatio = 1920 / w;
					const vRatio = 1080 / h;
					const scale = Math.min(hRatio, vRatio, 1);
					return scale;
				};

				let scale = getScale(img.width, img.height);
				let width = (c.width = img.width * scale);
				let height = (c.height = img.height * scale);
				ctx.save();
				if (orientation) {
					if (orientation > 4) {
						scale = getScale(img.height, img.width);
						width = c.width = img.width * scale;
						height = c.height = img.height * scale;
						c.width = height;
						c.height = width;
					}
					switch (orientation) {
						case 2:
							ctx.translate(width, 0);
							ctx.scale(-1, 1);
							break;
						case 3:
							ctx.translate(width, height);
							ctx.rotate(Math.PI);
							break;
						case 4:
							ctx.translate(0, height);
							ctx.scale(1, -1);
							break;
						case 5:
							ctx.rotate(0.5 * Math.PI);
							ctx.scale(1, -1);
							break;
						case 6:
							ctx.rotate(0.5 * Math.PI);
							ctx.translate(0, -height);
							break;
						case 7:
							ctx.rotate(0.5 * Math.PI);
							ctx.translate(width, -height);
							ctx.scale(-1, 1);
							break;
						case 8:
							ctx.rotate(-0.5 * Math.PI);
							ctx.translate(-width, 0);
							break;
					}
				}

				ctx.drawImage(img, 0, 0, width, height);
				ctx.restore();

				URL.revokeObjectURL(img.src); // we do not need the image anymore

				const isHorizontal = c.width > c.height;
				c.toBlob(
					function (blob) {
						resolve({ blob, mimeType, isHorizontal });
					},
					mimeType,
					0.85
				);
			});
		}.bind(this);
		img.onerror = (e) => reject(e);
		img.src = URL.createObjectURL(file);
	});
}

export async function saveDrawing(
		id,
		blob,
		inspectionId,
		fileName = new Date(),
		isHorizontal,
		mimeType,
		drawing,
		backgroundId,
		propertyName
) {
	_checkInspectionId(inspectionId);

	let d = {};
	d.id = id || (await getNewDrawingId());
	d.data = blob;
	d.drawing = drawing;
	d.inspectionId = inspectionId;
	d.dataMimeType = mimeType;
	d.isHorizontal = isHorizontal;
	d.name = fileName;
	d.date = new Date();
	d.propertyName = "drawing";
	d.backgroundId = backgroundId;

	let currentDrawings = await getAllDrawings(inspectionId);
	let max = -1;
	for (var drw of currentDrawings) {
		if (drw.propertyName.startsWith(d.propertyName)) {
			let i = drw.propertyName.replace(d.propertyName, "") || 0;
			max = Math.max(max, parseInt(i, 10));
		}
	}
	if (propertyName) {
		d.propertyName = propertyName
	} else {
		d.propertyName += max === -1 ? "" : max + 1;
	}

	let prevDrv = await db.drawings.get({ id: d.id });
	let prevExists = (prevDrv && prevDrv._key);
	if (prevExists) {
		d._key = prevDrv._key;
		d.description = prevDrv.description;
	}
	await db.drawings.put(d);

	window.sync.postMessage({
		name: prevExists ? 'drawingUpdated' : 'drawingAdded',
		value: { inspectionId: inspectionId, drawingId: d.id },
	});
}

export async function saveImages(inspId, detail) {
	const addedImageIds = await Promise.all(detail.files.map(async (file) => {
		const i = await convertPhoto(file);
		const imgId = await saveImage(i.blob, detail.context, inspId, file.name, i.isHorizontal, i.mimeType);
		return imgId;
	}));

	window.sync.postMessage({
		name: "imagesAdded",
		value: { inspectionId: inspId, imageIds: addedImageIds },
	});
}

function saveImage(blob, context, inspectionId, fileName, isHorizontal, mimeType) {
	_checkInspectionId(inspectionId);

	let d = new Date();

	return db.transaction("rw", db.inspections, db.images, async () => {
		let l = await db.images
			.filter(a => {
				return (
					a.inspectionId === inspectionId &&
                    a.context === context &&
                    a.propertyName != undefined &&
                    a.propertyName.endsWith(fileName)
				);
			})
			.count();

		let pref = "";
		if (l > 0)
			pref = l;

		let id = await getNewImageId();
		await db.inspections.get(inspectionId).then(i => {
			let contextObj = getNestedProperty(i, context);
			if (contextObj == undefined) {
				contextObj = setNestedProperty(i, context, {});
			}

			if (!i.cover_image_id) {
				i.cover_image_id = id;
			}

			contextObj.connected_images = contextObj.connected_images || {};
			contextObj.connected_images[id] = id;
			return db.inspections.put(i);
		});

		let imgId = await db.images.add({
			id,
			data: blob,
			propertyName: pref + fileName,
			dataMimeType: mimeType,
			date: d,
			name: d.toLocaleDateString(),
			inspectionId: inspectionId,
			isHorizontal: isHorizontal
		});

		return imgId;
	})
		.catch(a => console.log(a));
}

export function deleteDrawing(id, inspectionId) {
	return db.drawings
		.delete(id)
		.then(() => {
			window.sync.postMessage({
				name: 'drawingDeleted',
				value: { inspectionId: inspectionId, drawingId: id },
			});
		})
		.catch(a => console.log(a));
}

export function deleteImage(id, inspectionId) {
	return db
		.transaction("rw", db.inspections, db.images, async () => {
			let img = await db.images.get(id);
			if (img && img.inspectionId != inspectionId) throw new Error("inspectionId doesn't match inspectionId from image object");

			await db.images.delete(id);

			return db.inspections.get(inspectionId).then(i => {
				let index = findByProp(i, "connected_images");
				let ctx = index[id];
				let contextObj = getNestedProperty(i, ctx);

				let updated = false;
				if (contextObj.connected_images) {
					delete contextObj.connected_images[id];
					updated = true;
				}
				let promise = Promise.resolve();
				if (i.cover_image_id === id) {
					promise = db.images.where({ inspectionId: inspectionId }).first()
						.then((img) => {
							i.cover_image_id = img ? img.id : '';
							updated = true;
						});
				}
				return promise.then(() => updated ? db.inspections.put(i) : i.inspectionId);
			});
		})
		.then(() => {
			window.sync.postMessage({
				name: "imagesDeleted",
				value: { inspectionId: inspectionId, imageIds: [id] }
			});
		})
		.catch(a => console.error(a));
}

function getRemoteCover(images) {
	let x = images.filter(a => a.hasOwnProperty("cover") && a.cover);
	if (x.length > 0) {
		return x[0];
	} else {
		return images[0];
	}
}

export function getImagesForPdf(inspectionId, remote) {
	var coverQuery = getCoverImage(inspectionId);
	var imagesQuery = db.images.where('inspectionId').equals(inspectionId).toArray();
	var drawingsQuery = db.drawings.where('inspectionId').equals(inspectionId).toArray();

	if (remote) {
		let images = remote.images.map(img => {
			const uint8Array = new Uint8Array(img.dataArray);
			let i = { ...img };
			i.data = new Blob([uint8Array], { type: img.dataMimeType });
			delete i.dataArray;
			return i;
		});
		coverQuery = Promise.resolve(getRemoteCover(images));
		imagesQuery = Promise.resolve(images);
		drawingsQuery = Promise.resolve(remote.drawings);
	}


	function prepareDrawing(d) {
		if (!d) {
			return Promise.resolve();
		}
		return new Promise((resolve) => {
			var c = document.createElement("canvas");
			document.body.appendChild(c)
			let canvas = new fabric.Canvas(c, { preserveObjectStacking: true })

			canvas.loadFromJSON(d.drawing, () => {
				const origSize = canvas.initialSize;
				const wRatio = origSize.w / (595.28 * 2);
				const hRatio = origSize.h / (841.89 * 2);
				const ratio = Math.max(wRatio, hRatio);
				const zoom = 1 / ratio;

				canvas.setZoom(zoom);
				canvas.setWidth(origSize.w * zoom);
				canvas.setHeight(origSize.h * zoom);

				const resImg = {
					id: d.id,
					propertyName: d.propertyName,
					description: d.description,
					isHorizontal: d.isHorizontal,
					dataUrl: canvas.toDataURL({ format: 'jpeg', quality: 0.95 })
				};
				canvas.dispose();
				canvas = null;
				c.remove();
				c = null;
				resolve(resImg)
			});
		});
	}

	return Promise.all([coverQuery, imagesQuery, drawingsQuery]).then(async (dbQueries) => {
		const coverPromise = _convertImageToBase64Jpeg(dbQueries[0]);
		const imagePromises = [];
		dbQueries[1].forEach(i => {
			imagePromises.push(_convertImageToBase64Jpeg(i));
		});
		const drawingPromises = [];
		dbQueries[2].forEach(d => {
			drawingPromises.push(prepareDrawing(d));
		});

		const promises = [].concat(coverPromise, imagePromises, drawingPromises);

		const images = await Promise.all(promises);
		const result = {
			cover: images[0],
			images: imagePromises.length
				? images.slice(1, imagePromises.length + 1)
				: [],
			drawings: drawingPromises.length
				? images.slice(images.length - drawingPromises.length)
				: []
		};
		let canvasContainers = document.getElementsByClassName('canvas-container')
		Array.from(canvasContainers).map(c => c.remove())
		return result;
	});
}

export function updateImage(id, updatedData) {
	id = Number(id);
	return db.images.update(id, updatedData)
		.then(updCount => {
			return updCount > 0 ? db.images.get(id) : null;
		})
		.then(img => {
			if (img) {
				window.sync.postMessage({
					name: "imageUpdated",
					value: { inspectionId: img.inspectionId, imageId: img.id },
				});
			}
		})
		.catch(a => console.log(a));
}

export function updateDrawing(id, updatedData) {
	id = Number(id);
	return db.drawings.update(id, updatedData)
		.then(updCount => {
			return updCount > 0 ? db.drawings.get(id).then(drw => drw.inspectionId) : null;
		})
		.then(inspId => {
			if (inspId) {
				window.sync.postMessage({
					name: 'drawingUpdated',
					value: { inspectionId: inspId, drawingId: id },
				});
			}
		})
		.catch(a => console.log(a));
}

export function getSignatureDrawing(inspectionId) {
	return db.drawings.filter(d => d.inspectionId === inspectionId && d.propertyName === 'signature').first();
}

export async function copyConnectedImages(inspection, value) {
	const addedImageIds = [];

	await db.transaction("rw", db.inspections, db.images, async () => {
		for (const i in value.connected_images) {
			if (value.connected_images.hasOwnProperty(i)) {
				const imgId = value.connected_images[i];
				const newId = await db.images.get({ id: imgId })
					.then(async (img) => {
						img.id = await getNewImageId();
						delete img._key;
						delete img._id;
						delete img._rev;
						return db.images.add(img);
					});
				delete value.connected_images[imgId];
				value.connected_images[newId] = newId;
				addedImageIds.push(newId);
			}
		}
		await db.inspections.put(inspection);
	});

	window.sync.postMessage({
		name: "imagesAdded",
		value: { inspectionId: inspection._key, imageIds: addedImageIds },
	});

	return inspection._key;
}

export async function deleteConnectedImages(changeObj, value) {
	const deletedImageIds = [];
	for (const i in value.connected_images) {
		if (value.connected_images.hasOwnProperty(i)) {
			deletedImageIds.push(value.connected_images[i]);
		}
	}
	await db.transaction("rw", db.inspections, db.images, async () => {
		for (const id of deletedImageIds) {
			await db.images.delete(id);
		}
		await db.inspections.put(changeObj);
	});

	window.sync.postMessage({
		name: "imagesDeleted",
		value: { inspectionId: changeObj._key, imageIds: deletedImageIds },
	});

	return changeObj._key;
}

export async function checkConnectedImages(inspection) { 
	const msg = 'Connected images check is failed';
	try {
		const index = await getContextsIndexForInsp(inspection, 'images');
		if (index) {
			const imgIds = Object.getOwnPropertyNames(index).map(i => Number(i));
			for (const imgId of imgIds) {
				let img = await db.images.get(imgId);
				let jpegImg = await _convertImageToBase64Jpeg(img);
				let heathy = !!jpegImg.dataUrl;
				if (!heathy) {
					console.error(msg + '. The image wasn`t converted to Base64Jpeg', imgId, img);
					return false;			
				}
			}
		}	
	} catch (error) {
		console.error(msg, error);
		return false;
	}
	return true;
}

export async function getAllImagesByDbAndContext(inspectionId) {
	const dbImages = await getAllImages(inspectionId);
	const dbImageIds = dbImages.map(i => i.id);

	const index = await getContextsIndex(inspectionId, 'images') || {};
	Object.getOwnPropertyNames(index).map(i => Number(i)).forEach(contextImgId => {
		if (dbImageIds.indexOf(contextImgId) === -1) {
			dbImages.push({ id: contextImgId, propertyName: '' });
		}
	});
	
	return dbImages;
}